import type { EnvironmentType } from '@post-office/shared-contracts/execution-runtime';
import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context/requestContext';
import { getJiraOnboardingUrl } from '@post-office/message-templates-common--growth-starburst/utils/get-jira-onboarding-url';
import { type RedirectTo } from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

export {
	resolveRedirectTo,
	type RedirectTo,
} from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

const LOOM_ONBOARDING_URL_PROD = 'https://www.loom.com/meetings';
const LOOM_ONBOARDING_URL_NON_PROD = 'https://stage.loom.com/meetings';

// Unified Rovo create surface — tenant-agnostic, AI-first Confluence create experience.
const CONFLUENCE_CREATE_WITH_ROVO_URL = 'https://create.rovo.com';

export const getLoomOnboardingUrl = (environmentType: EnvironmentType): string => {
	return environmentType === 'Production' ? LOOM_ONBOARDING_URL_PROD : LOOM_ONBOARDING_URL_NON_PROD;
};

type GetOnboardingUrlParams = {
	redirectTo: RedirectTo;
	requestContext: RequestContext;
	messageInstanceId: string;
	messageTemplateId: string;
	orgId?: string;
	twcEntitlementId?: string;
};

/**
 * Returns the onboarding URL for the personalized missing-app banner CTA.
 *
 * - Jira routes through the tenant-aware GUPS helper.
 * - Confluence routes to the tenant-agnostic Rovo Create surface.
 * - Loom is handled by callers via `getLoomOnboardingUrl` because
 * the destination depends only on environment, not on request context or flow.
 */
export const getOnboardingUrl = async ({
	redirectTo,
	requestContext,
	messageInstanceId,
	messageTemplateId,
	orgId,
	twcEntitlementId,
}: GetOnboardingUrlParams): Promise<string | undefined> => {
	switch (redirectTo) {
		case 'jira':
			return getJiraOnboardingUrl(
				requestContext,
				orgId,
				twcEntitlementId,
				messageInstanceId,
				messageTemplateId,
			);

		case 'confluence':
			return CONFLUENCE_CREATE_WITH_ROVO_URL;

		default: {
			const _exhaustive: never = redirectTo;
			return _exhaustive;
		}
	}
};
